import * as RadixToast from '@radix-ui/react-toast';
import { cx, cva, VariantProps } from 'class-variance-authority';
import React, { createContext, useCallback, useContext, useState } from 'react';
import { formatTxn } from './format-txn';

type TypeToast = {
  title: React.ReactNode | string;
  description?: string;
  type: 'success' | 'error' | 'warning' | 'info';
  icon?: string;
  hash?: string;
  time?: string;
  href?: string;
};

const typeWrapperClasses = cva(['rounded-md '], {
  variants: {
    type: {
      default: 'text-white',
      success: 'border border-green-25',
      accent: 'text-green-25',
      info: 'text-white',
      warning: 'border border-amber-700',
      error: 'border border-amber-700',
    },
  },
  defaultVariants: {
    type: 'default',
  },
});

const typeClasses = cva(['rounded-md '], {
  variants: {
    type: {
      default: 'text-white',
      success: 'text-green-25',
      accent: 'text-green-25',
      info: 'text-white',
      warning: 'text-amber-700',
      error: 'text-error',
    },
  },
  defaultVariants: {
    type: 'default',
  },
});

const copyText = (text: string): void => {
  navigator.clipboard.writeText(text);
};

function Toast(
  props: TypeToast &
    VariantProps<typeof typeWrapperClasses> &
    VariantProps<typeof typeClasses> & {
      index: number;
      hash?: string;
      time?: string;
      icon?: string;
      href?: string;
    }
) {
  const { title, description, index, type, hash, time, icon, href } = props;

  return (
    <RadixToast.Root
      className={cx(
        'shadow-dropdown w-80 max-w-md gap-4 bg-backgroundPrimary px-2 pb-2 pt-2 text-white',
        typeWrapperClasses({ type }),
        icon ? 'grid grid-cols-[40px_1fr] ' : 'gird-cols-1 grid'
      )}
      key={index}
    >
      {icon && (
        <div
          className={cx(
            'm-1 flex h-full w-12 grow flex-col place-content-center items-center',
            typeClasses({ type })
          )}
        >
          <span className='material-symbols-outlined'> {icon}</span>
        </div>
      )}
      <div className='flex grow flex-col gap-1'>
        <RadixToast.Title className='w-72 break-words text-base font-normal text-white'>
          {title}
        </RadixToast.Title>
        {description && (
          <RadixToast.Description className='ToastDescription m-0 w-72 break-words text-sm leading-4 text-white'>
            {description}
          </RadixToast.Description>
        )}
        {hash && href && (
          <div className='flex w-72 flex-row items-center break-words pb-1 pt-1'>
            <div className='text-xs text-white'>
              <a href={href} target='_blank' rel='noreferrer noopener'>
                {formatTxn(hash)}
              </a>
            </div>
            {time && <div className='text-sm text-slate-600'>- {time} ago</div>}
          </div>
        )}
        {hash && !href && (
          <div
            className='line-clamp-2 w-14 cursor-pointer text-xs text-blue-400 hover:text-blue-200'
            onClick={() => copyText(hash)}
          >
            {formatTxn(hash)}{' '}
          </div>
        )}
      </div>
    </RadixToast.Root>
  );
}

const ToastContext = createContext<null | ((toast: TypeToast) => void)>(null);

export function useToast() {
  const toast = useContext(ToastContext);
  if (toast == null) throw 'No toast context';
  return toast;
}

export function ToastProvider({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const [toasts, setToasts] = useState<TypeToast[]>([]);

  const addToast = useCallback((toast: TypeToast) => {
    setToasts((toasts) => [...toasts, toast]);
  }, []);

  return (
    <RadixToast.Provider>
      <ToastContext.Provider value={addToast}>{children}</ToastContext.Provider>
      {toasts.map((toast, index) => (
        <Toast key={index} {...toast} index={index} />
      ))}
      <RadixToast.Viewport
        className={cx(
          'fixed bottom-5 right-5 z-[100] m-0 flex w-96 max-w-fit list-none flex-col gap-2 outline-none',
          className
        )}
      />
    </RadixToast.Provider>
  );
}

interface EmojiProps {
  className?: string;
  label?: string;
  symbol: number;
}

export const Emoji = React.memo<EmojiProps>(
  ({
    className,
    label,
    symbol,
  }: {
    className?: string;
    label?: string;
    symbol: number;
  }) => (
    <span className={className} role='img' aria-label={label}>
      {String.fromCodePoint(symbol)}
    </span>
  )
);
